import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import ReactDOMServer from 'react-dom/server';
import 'leaflet/dist/leaflet.css';
import 'leaflet-routing-machine';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css';
import * as Styled from './styles';
import createCooperplaceRouter from '../router/createCooperplaceRouter';
import { createWaypointLetterIcon } from '../utils/markerIcons';
import {
  bindIntermediateWaypointRemoval,
  createIntermediateWaypointIcon,
} from '../utils/intermediateWaypoints';
import { MarkerContainer } from 'v3/components/Leaflet/MarkerTollsPlaza/styles';
import { PopupContainer } from 'v3/components/Leaflet/MarkerTollsPlazaInfo/styles';

const isValidPoint = point =>
  point &&
  typeof point.lat === 'number' &&
  !Number.isNaN(point.lat) &&
  typeof point.lng === 'number' &&
  !Number.isNaN(point.lng);

const isBusinessPoint = point =>
  isValidPoint(point) && point?.type?.id !== 'via' && point?.action?.id !== 'via';

const getDistanceScore = (point, waypoint) => {
  if (!point || !waypoint?.latLng) return Number.POSITIVE_INFINITY;

  const latDelta = Number(point.lat) - Number(waypoint.latLng.lat);
  const lngDelta = Number(point.lng) - Number(waypoint.latLng.lng);

  return (latDelta * latDelta) + (lngDelta * lngDelta);
};

const matchBusinessPointIndexes = (businessPoints = [], waypoints = []) => {
  if (businessPoints.length === 0 || waypoints.length === 0) return [];
  if (businessPoints.length === 1) return [0];

  const matchedIndexes = [0];
  let searchStartIndex = 1;

  for (let businessIndex = 1; businessIndex < businessPoints.length - 1; businessIndex += 1) {
    const point = businessPoints[businessIndex];
    const remainingBusinessPoints = businessPoints.length - businessIndex - 1;
    const lastCandidateIndex = waypoints.length - remainingBusinessPoints - 1;

    let bestWaypointIndex = searchStartIndex;
    let bestDistanceScore = Number.POSITIVE_INFINITY;

    for (let waypointIndex = searchStartIndex; waypointIndex <= lastCandidateIndex; waypointIndex += 1) {
      const distanceScore = getDistanceScore(point, waypoints[waypointIndex]);

      if (distanceScore < bestDistanceScore) {
        bestDistanceScore = distanceScore;
        bestWaypointIndex = waypointIndex;
      }
    }

    matchedIndexes.push(bestWaypointIndex);
    searchStartIndex = bestWaypointIndex + 1;
  }

  matchedIndexes.push(waypoints.length - 1);

  return matchedIndexes;
};

const buildPointsKey = points =>
  (points || [])
    .map(point => `${point.lat.toFixed(6)},${point.lng.toFixed(6)}`)
    .join('|');

const getLatLngPoint = value => {
  const lat = typeof value?.lat === 'function' ? value.lat() : value?.lat;
  const lng = typeof value?.lng === 'function' ? value.lng() : value?.lng;

  if (typeof lat !== 'number' || Number.isNaN(lat)) return null;
  if (typeof lng !== 'number' || Number.isNaN(lng)) return null;

  return { lat, lng };
};

const getPointSegmentDistanceScore = (point, start, end) => {
  const segmentLat = end.lat - start.lat;
  const segmentLng = end.lng - start.lng;
  const segmentLength = (segmentLat * segmentLat) + (segmentLng * segmentLng);

  if (segmentLength === 0) {
    const latDelta = point.lat - start.lat;
    const lngDelta = point.lng - start.lng;
    return (latDelta * latDelta) + (lngDelta * lngDelta);
  }

  const rawProjection =
    ((point.lat - start.lat) * segmentLat + (point.lng - start.lng) * segmentLng) /
    segmentLength;
  const projection = Math.max(0, Math.min(1, rawProjection));
  const projectedLat = start.lat + projection * segmentLat;
  const projectedLng = start.lng + projection * segmentLng;
  const latDelta = point.lat - projectedLat;
  const lngDelta = point.lng - projectedLng;

  return (latDelta * latDelta) + (lngDelta * lngDelta);
};

const getClosestWaypointSegmentIndex = (waypoints = [], latLng) => {
  const point = getLatLngPoint(latLng);
  if (!point || waypoints.length < 2) return 0;

  let closestSegmentIndex = 0;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (let index = 0; index < waypoints.length - 1; index += 1) {
    const start = getLatLngPoint(waypoints[index]?.latLng || waypoints[index]);
    const end = getLatLngPoint(waypoints[index + 1]?.latLng || waypoints[index + 1]);
    if (!start || !end) continue;

    const distance = getPointSegmentDistanceScore(point, start, end);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestSegmentIndex = index;
    }
  }

  return closestSegmentIndex;
};

const getTollCoordinate = (toll, fieldNames = []) => {
  for (const fieldName of fieldNames) {
    const value = Number(toll?.[fieldName]);
    if (!Number.isNaN(value)) return value;
  }

  return null;
};

const encodePolyline = (coordinates = []) => {
  let lastLat = 0;
  let lastLng = 0;
  let result = '';

  for (const [lat, lng] of coordinates) {
    const latE5 = Math.round(lat * 1e5);
    const lngE5 = Math.round(lng * 1e5);

    let dLat = latE5 - lastLat;
    let dLng = lngE5 - lastLng;

    lastLat = latE5;
    lastLng = lngE5;

    [dLat, dLng].forEach(value => {
      let v = value < 0 ? ~(value << 1) : value << 1;
      while (v >= 0x20) {
        result += String.fromCharCode((0x20 | (v & 0x1f)) + 63);
        v >>= 5;
      }
      result += String.fromCharCode(v + 63);
    });
  }

  return result;
};

export default function RouteMachineMap({
  points = [],
  tollsPlazaInfos = null,
  hideTolls = false,
  onWaypointsChange,
  onRouteCalculated,
  onRoutingStateChange,
  onRouteStatus,
  canDragPoint,
  allowWaypointEditing = true,
  readOnly = false,
  showReadOnlyOverlay = readOnly,
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const routerRef = useRef(null);
  const routingControlRef = useRef(null);
  const pointsRef = useRef(points);
  const readOnlyRef = useRef(readOnly);
  const canDragPointRef = useRef(canDragPoint);
  const onWaypointsChangeRef = useRef(onWaypointsChange);
  const onRouteCalculatedRef = useRef(onRouteCalculated);
  const onRoutingStateChangeRef = useRef(onRoutingStateChange);
  const onRouteStatusRef = useRef(onRouteStatus);
  const fullscreenControlRef = useRef(null);
  const fullscreenButtonRef = useRef(null);
  const lastWaypointsKeyRef = useRef('');
  const skipNextWaypointsChangedRef = useRef(false);
  const tollMarkersLayerRef = useRef(null);
  const tollMarkersRef = useRef(new Map());
  const tollInfoMarkerRef = useRef(null);
  const selectedTollCodeRef = useRef(null);
  const tollsHiddenRef = useRef(false);
  const hideTollsRef = useRef(hideTolls);

  const getTollOpacity = zoom => {
    if (zoom <= 6) return 0.6;
    if (zoom <= 9) return 0.95;
    return 1;
  };

  const createTollMarkerIcon = index =>
    L.divIcon({
      className: 'toll-plaza-marker',
      html: ReactDOMServer.renderToString(
        <MarkerContainer background="#d32f2f">
          <span className="marker-index">{index + 1}</span>
        </MarkerContainer>,
      ),
      iconSize: [24, 30],
      iconAnchor: [11, 30],
    });

  const clearSelectedTollInfo = () => {
    if (tollInfoMarkerRef.current) {
      tollInfoMarkerRef.current.remove();
      tollInfoMarkerRef.current = null;
    }

    selectedTollCodeRef.current = null;
  };

  const setTollsVisibility = hidden => {
    tollsHiddenRef.current = hidden;

    const tollPane = mapInstanceRef.current?.getPane?.('tollPlazaPane');
    if (tollPane) {
      tollPane.style.display = hidden ? 'none' : '';
    }

    tollMarkersRef.current.forEach(marker => {
      const element = marker?.getElement?.();
      if (element) {
        element.style.display = hidden ? 'none' : '';
      }
    });

    const tollInfoElement = tollInfoMarkerRef.current?.getElement?.();
    if (tollInfoElement) {
      tollInfoElement.style.display = hidden ? 'none' : '';
    }
  };

  const createTollInfoIcon = toll =>
    L.divIcon({
      className: '',
      html: ReactDOMServer.renderToString(
        <PopupContainer>
          <div style={{ fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
            {toll?.name}
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
            {toll?.address}
          </div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              textAlign: 'center',
              color: '#a72828ff',
            }}
          >
            R$: {Number(toll?.tollValue || 0).toFixed(2)}
          </div>
        </PopupContainer>,
      ),
      iconAnchor: [110, 0],
    });

  const attachLineTouchedWaypointInsertion = (routingControl) => {
    const line = routingControl?._line;
    if (!line?.off || !line?.on) return;

    line.off('linetouched');
    line.on('linetouched', (event) => {
      if (readOnlyRef.current || !allowWaypointEditing) return;

      const waypointLatLng = event?.latlng;
      const currentWaypoints = routingControl.getWaypoints?.() || [];
      const closestSegmentIndex = getClosestWaypointSegmentIndex(
        currentWaypoints,
        waypointLatLng,
      );

      if (!waypointLatLng) return;

      routingControl.spliceWaypoints(closestSegmentIndex + 1, 0, waypointLatLng);
    });
  };

  useEffect(() => {
    pointsRef.current = points;
  }, [points]);

  useEffect(() => {
    readOnlyRef.current = readOnly;
  }, [readOnly]);

  useEffect(() => {
    canDragPointRef.current = canDragPoint;
  }, [canDragPoint]);

  useEffect(() => {
    onWaypointsChangeRef.current = onWaypointsChange;
  }, [onWaypointsChange]);

  useEffect(() => {
    onRouteCalculatedRef.current = onRouteCalculated;
  }, [onRouteCalculated]);

  useEffect(() => {
    onRoutingStateChangeRef.current = onRoutingStateChange;
  }, [onRoutingStateChange]);

  useEffect(() => {
    onRouteStatusRef.current = onRouteStatus;
  }, [onRouteStatus]);

  useEffect(() => {
    hideTollsRef.current = hideTolls;
    if (!hideTolls) {
      setTollsVisibility(false);
    }
  }, [hideTolls]);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      zoomControl: true,
    }).setView([-15.793889, -47.882778], 5);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    map.createPane('tollPlazaPane');
    const tollPane = map.getPane('tollPlazaPane');
    if (tollPane) {
      tollPane.style.zIndex = 650;
      tollPane.style.pointerEvents = 'auto';
    }

    tollMarkersLayerRef.current = L.layerGroup().addTo(map);
    map.on('click', clearSelectedTollInfo);

    const validPoints = (points || []).filter(isValidPoint);
    const defaultWaypoints = validPoints.length >= 1
      ? validPoints.map(p => L.latLng(p.lat, p.lng))
      : [];

    const updateFullscreenButton = () => {
      if (!fullscreenButtonRef.current) return;

      const fsElement = document.fullscreenElement || document.webkitFullscreenElement || null;
      const isFullscreen = fsElement === mapRef.current;

      fullscreenButtonRef.current.innerHTML = isFullscreen ? '⤫' : '⛶';
      fullscreenButtonRef.current.title = isFullscreen ? 'Sair da tela cheia' : 'Tela cheia';
    };

    const toggleFullscreen = async () => {
      const mapContainer = mapRef.current;
      if (!mapContainer) return;

      const fsElement = document.fullscreenElement || document.webkitFullscreenElement || null;
      const isFullscreen = fsElement === mapContainer;

      if (isFullscreen) {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
        return;
      }

      if (mapContainer.requestFullscreen) {
        await mapContainer.requestFullscreen();
      } else if (mapContainer.webkitRequestFullscreen) {
        mapContainer.webkitRequestFullscreen();
      }
    };

    const handleFullscreenChange = () => {
      updateFullscreenButton();

      if (mapInstanceRef.current) {
        setTimeout(() => {
          mapInstanceRef.current.invalidateSize();
        }, 0);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    const router = createCooperplaceRouter();
    routerRef.current = router;

    let routingControl = null;

    routingControl = L.Routing.control({
      waypoints: defaultWaypoints,
      router,
      routeWhileDragging: true,
      show: false,
      addWaypoints: allowWaypointEditing,
      draggableWaypoints: allowWaypointEditing,
      fitSelectedRoutes: true,
      showAlternatives: false,
      lineOptions: {
        styles: [
          { color: 'black', opacity: 0.15, weight: 9 },
          { color: 'white', opacity: 0.8, weight: 6 },
          { color: 'red', opacity: 1, weight: 2 },
        ],
        addWaypoints: allowWaypointEditing,
        extendToWaypoints: true,
        missingRouteTolerance: 0,
      },
      altLineOptions: {
        styles: [
          { color: 'black', opacity: 0.15, weight: 9 },
          { color: 'white', opacity: 0.8, weight: 6 },
          { color: '#6FA1EC', opacity: 0.5, weight: 2 },
        ],
      },
      createMarker: (index, waypoint) => {
        const validPoints = (pointsRef.current || []).filter(isValidPoint);
        const businessPoints = validPoints.filter(isBusinessPoint);
        const routingWaypoints = routingControlRef.current?.getWaypoints?.() || [];
        const businessWaypointIndexes = matchBusinessPointIndexes(
          businessPoints,
          routingWaypoints,
        );
        const businessPointCursor = businessWaypointIndexes.indexOf(index);
        let businessPoint = null;
        let interestPointIndex = -1;

        if (businessPointCursor >= 0) {
          interestPointIndex = businessPointCursor;
          businessPoint = businessPoints[businessPointCursor] || null;
        }

        const isIntermediateWaypoint = interestPointIndex < 0;
        const canDragBusinessPoint =
          typeof canDragPointRef.current === 'function'
            ? canDragPointRef.current(businessPoint)
            : true;

        const marker = L.marker(waypoint.latLng, {
          draggable: isIntermediateWaypoint ? allowWaypointEditing : canDragBusinessPoint,
          icon: isIntermediateWaypoint
            ? createIntermediateWaypointIcon()
            : createWaypointLetterIcon({
                point: businessPoint,
                interestPointIndex,
                total: businessPoints.length,
              }),
        });

        if (isIntermediateWaypoint && allowWaypointEditing) {
          bindIntermediateWaypointRemoval({
            marker,
            onRemove: () => {
              if (!routingControlRef.current) return;
              routingControlRef.current.spliceWaypoints(index, 1);
            },
          });
        }

        marker.on('dragstart', () => {
          clearSelectedTollInfo();
          setTollsVisibility(true);
        });

        marker.on('dragend', () => {
          if (!hideTollsRef.current) {
            setTollsVisibility(false);
          }
        });

        return marker;
      },
    });

    routingControlRef.current = routingControl;
    routingControl.addTo(map);

    routingControl.on('waypointschanged', event => {
      if (skipNextWaypointsChangedRef.current) {
        skipNextWaypointsChangedRef.current = false;
        return;
      }

      if (readOnlyRef.current) return;
      if (typeof onWaypointsChangeRef.current !== 'function') return;

      const waypoints =
        event?.waypoints || routingControl.getWaypoints?.() || [];

      const normalizedWaypoints = waypoints
        .map(waypoint => waypoint?.latLng)
        .filter(Boolean)
        .map(latLng => ({ lat: latLng.lat, lng: latLng.lng }));

      if (normalizedWaypoints.length < 2) return;
      lastWaypointsKeyRef.current = buildPointsKey(normalizedWaypoints);
      onWaypointsChangeRef.current(normalizedWaypoints);
    });

    routingControl.on('routingstart', event => {
      if (typeof onRoutingStateChangeRef.current !== 'function') return;

      onRoutingStateChangeRef.current({
        status: 'start',
        waypoints: event?.waypoints || routingControl.getWaypoints?.() || [],
      });
    });

    routingControl.on('routesfound', event => {
      attachLineTouchedWaypointInsertion(routingControl);

      if (!hideTollsRef.current) {
        setTollsVisibility(false);
      }

      if (typeof onRouteStatusRef.current === 'function') {
        onRouteStatusRef.current({
          hasRouteError: false,
          hasNoRouteInRegion: false,
          errorPointIds: [],
        });
      }

      const routeCoordinates = (event?.routes?.[0]?.coordinates || [])
        .map(coordinate => [coordinate?.lat, coordinate?.lng])
        .filter(
          ([lat, lng]) =>
            typeof lat === 'number' &&
            !Number.isNaN(lat) &&
            typeof lng === 'number' &&
            !Number.isNaN(lng),
        );

      const encodedPolyline =
        routeCoordinates.length >= 2 ? encodePolyline(routeCoordinates) : null;
      const routePayload = {
        coordinates: routeCoordinates,
        encodedPolyline,
        route: event?.routes?.[0] || null,
      };

      if (typeof onRoutingStateChangeRef.current === 'function') {
        onRoutingStateChangeRef.current({
          status: 'success',
          ...routePayload,
        });
      }

      if (typeof onRouteCalculatedRef.current !== 'function') return;

      if (routeCoordinates.length < 2) {
        onRouteCalculatedRef.current({ coordinates: [], encodedPolyline: null });
        return;
      }

      onRouteCalculatedRef.current(routePayload);
    });

    routingControl.on('routeselected', event => {
      if (typeof onRoutingStateChangeRef.current !== 'function') return;

      const routeCoordinates = (event?.route?.coordinates || [])
        .map(coordinate => [coordinate?.lat, coordinate?.lng])
        .filter(
          ([lat, lng]) =>
            typeof lat === 'number' &&
            !Number.isNaN(lat) &&
            typeof lng === 'number' &&
            !Number.isNaN(lng),
        );

      onRoutingStateChangeRef.current({
        status: 'selected',
        coordinates: routeCoordinates,
        encodedPolyline:
          routeCoordinates.length >= 2 ? encodePolyline(routeCoordinates) : null,
        route: event?.route || null,
      });
    });

    routingControl.on('routingerror', event => {
      const businessPointIds = (pointsRef.current || [])
        .filter(point => isBusinessPoint(point))
        .map(point => point?.identifier || point?.id)
        .filter(Boolean);

      if (typeof onRoutingStateChangeRef.current === 'function') {
        onRoutingStateChangeRef.current({
          status: 'error',
          error: event?.error || null,
          errorPointIds: businessPointIds,
        });
      }

      if (typeof onRouteStatusRef.current === 'function') {
        onRouteStatusRef.current({
          hasRouteError: true,
          hasNoRouteInRegion: true,
          errorPointIds: businessPointIds,
        });
      }

      if (typeof onRouteCalculatedRef.current !== 'function') return;
      onRouteCalculatedRef.current({ coordinates: [], encodedPolyline: null });
    });

    routingControlRef.current = routingControl;
    lastWaypointsKeyRef.current = validPoints.length >= 1 ? buildPointsKey(validPoints) : '';

    const FullscreenControl = L.Control.extend({
      options: {
        position: 'topleft',
      },
      onAdd() {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        const button = L.DomUtil.create('a', '', container);

        button.href = '#';
        button.role = 'button';
        button.style.cursor = 'pointer';
        button.style.fontSize = '16px';
        button.style.textAlign = 'center';
        button.style.lineHeight = '26px';
        button.style.width = '30px';
        button.style.height = '30px';

        fullscreenButtonRef.current = button;
        updateFullscreenButton();

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.on(button, 'click', L.DomEvent.stop);
        L.DomEvent.on(button, 'click', toggleFullscreen);

        return container;
      },
    });

    const fullscreenControl = new FullscreenControl();
    map.addControl(fullscreenControl);
    fullscreenControlRef.current = fullscreenControl;

    mapInstanceRef.current = map;

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);

      if (routerRef.current && typeof routerRef.current.abortAll === 'function') {
        routerRef.current.abortAll();
      }
      if (fullscreenControlRef.current) {
        map.removeControl(fullscreenControlRef.current);
      }
      if (routingControlRef.current) {
        map.removeControl(routingControlRef.current);
      }
      if (tollMarkersLayerRef.current) {
        tollMarkersLayerRef.current.clearLayers();
      }
      tollMarkersRef.current.clear();
      clearSelectedTollInfo();
      map.off('click', clearSelectedTollInfo);
      fullscreenControlRef.current = null;
      fullscreenButtonRef.current = null;
      routerRef.current = null;
      routingControlRef.current = null;
      tollMarkersLayerRef.current = null;
      lastWaypointsKeyRef.current = '';
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [allowWaypointEditing]);

  useEffect(() => {
    if (!mapInstanceRef.current || !routingControlRef.current) return;
    if (!points || points.length === 0) {
      if (typeof onRouteStatusRef.current === 'function') {
        onRouteStatusRef.current({
          hasRouteError: false,
          hasNoRouteInRegion: false,
          errorPointIds: [],
        });
      }
      if (typeof onRouteCalculatedRef.current === 'function') {
        onRouteCalculatedRef.current({ coordinates: [], encodedPolyline: null });
      }

      if (lastWaypointsKeyRef.current !== '') {
        lastWaypointsKeyRef.current = '';
        routingControlRef.current.setWaypoints([]);
      }
      return;
    }

    const validPoints = points.filter(isValidPoint);

    if (validPoints.length === 0) {
      if (typeof onRouteStatusRef.current === 'function') {
        onRouteStatusRef.current({
          hasRouteError: false,
          hasNoRouteInRegion: false,
          errorPointIds: [],
        });
      }
      if (typeof onRouteCalculatedRef.current === 'function') {
        onRouteCalculatedRef.current({ coordinates: [], encodedPolyline: null });
      }

      if (lastWaypointsKeyRef.current !== '') {
        lastWaypointsKeyRef.current = '';
        routingControlRef.current.setWaypoints([]);
      }
      return;
    }

    if (validPoints.length === 1) {
      if (typeof onRouteStatusRef.current === 'function') {
        onRouteStatusRef.current({
          hasRouteError: false,
          hasNoRouteInRegion: false,
          errorPointIds: [],
        });
      }
      if (typeof onRouteCalculatedRef.current === 'function') {
        onRouteCalculatedRef.current({ coordinates: [], encodedPolyline: null });
      }
    }

    const nextWaypointsKey = buildPointsKey(validPoints);
    if (nextWaypointsKey === lastWaypointsKeyRef.current) return;

    lastWaypointsKeyRef.current = nextWaypointsKey;
    skipNextWaypointsChangedRef.current = true;
    routingControlRef.current.setWaypoints(validPoints.map(p => L.latLng(p.lat, p.lng)));

  }, [points]);

  useEffect(() => {
    if (!routingControlRef.current) return;

    const validPoints = (points || []).filter(isValidPoint);
    lastWaypointsKeyRef.current = '';
    skipNextWaypointsChangedRef.current = true;
    routingControlRef.current.setWaypoints(
      validPoints.map(p => L.latLng(p.lat, p.lng)),
    );
  }, [readOnly, allowWaypointEditing]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const tollLayer = tollMarkersLayerRef.current;
    const tolls = tollsPlazaInfos?.tollsPlaza;

    if (!map || !tollLayer) return;

    tollLayer.clearLayers();
    tollMarkersRef.current.clear();
    clearSelectedTollInfo();

    if (hideTolls) return;
    if (!Array.isArray(tolls) || tolls.length === 0) return;

    const markers = tolls
      .map((toll, index) => {
        const lat = getTollCoordinate(toll, ['lat', 'latitude']);
        const lng = getTollCoordinate(toll, ['long', 'lng', 'lon', 'longitude']);

        if (lat === null || lng === null) return null;

        const marker = L.marker([lat, lng], {
          pane: 'tollPlazaPane',
          icon: createTollMarkerIcon(index),
          opacity: getTollOpacity(map.getZoom()),
          zIndexOffset: 1000,
        });

        marker.on('click', () => {
          const tollCode = toll?.codeAntt || `${lat}-${lng}-${index}`;

          if (selectedTollCodeRef.current === tollCode) {
            clearSelectedTollInfo();
            return;
          }

          clearSelectedTollInfo();

          tollInfoMarkerRef.current = L.marker([lat, lng], {
            pane: 'tollPlazaPane',
            icon: createTollInfoIcon(toll),
            keyboard: false,
            zIndexOffset: 1000,
            interactive: false,
          }).addTo(map);

          selectedTollCodeRef.current = tollCode;
        });

        tollMarkersRef.current.set(`toll-${index}`, marker);

        return marker;
      })
      .filter(Boolean);

    markers.forEach(marker => marker.addTo(tollLayer));
    setTollsVisibility(false);

    const handleZoomEnd = () => {
      const opacity = getTollOpacity(map.getZoom());
      markers.forEach(marker => marker.setOpacity(opacity));
    };

    map.on('zoomend', handleZoomEnd);

    return () => {
      map.off('zoomend', handleZoomEnd);
      tollLayer.clearLayers();
    };
  }, [hideTolls, tollsPlazaInfos, allowWaypointEditing]);

  return (
    <Styled.MapContainer>
      <div ref={mapRef} style={{ height: '100%', width: '100%' }} />
      {showReadOnlyOverlay && <Styled.ReadOnlyOverlay aria-hidden="true" />}
    </Styled.MapContainer>
  );
}
